import {
  Button,
  Divider,
  TextField,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { SavedAddress } from "api/resources/payment/types"
import { OTPResponse } from "api/resources/settings/types"
import { FetchUserResponse } from "api/resources/users/types"
import { APIError } from "api/utils"
import CourseFallback from "assets/Placeholder/product-image-placeholder.svg"
import GoogleLocationSelect, {
  AddressComponents,
  AddressTypeEnum,
} from "components/GoogleLocationSelect.tsx"
import { AuthManager } from "features/Authentication/AuthManager"
import { getProductImage } from "features/LearningItems/getProductDisplayConfig"
import { NewAddress } from "features/Payment/AddAddress"
import SignInEmailDialog from "features/PgctlOnboarding/SignInEmailDialog"
import { useGlobalContext } from "global/Context/context"
import { useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-hook"
import { Controller, useForm, useWatch } from "react-hook-form"
import { CPAT_PRODUCT_SLUG } from "utils/constants"
import { mapErrorsV2, splitFullName } from "utils/helpers"
import { CaptchaType } from "utils/hooks/useGRecaptcha"
import { getCurrentPlatform } from "utils/platform"
import { createLead, trackingService } from "utils/tracking"
import isEmail from "validator/lib/isEmail"

type FormData = {
  location: AddressComponents | null
  email: string
  fullName: string | null
}

interface BillingFormProps {
  onAddressCreated: (address: SavedAddress) => void
}

const BillingForm = ({ onAddressCreated }: BillingFormProps) => {
  const authInfo = AuthManager.getPlatformAuth()

  const { isAuthenticated, setAuthInfo } = useGlobalContext()

  const [otpData, setOtpData] = useState<OTPResponse>()

  const [openSignInEmailDialog, setOpenSignInEmailDialog] = useState(false)

  const {
    register,
    control,
    setError,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      location: null,
      email: authInfo?.user?.email,
      fullName: null,
    },
  })

  const platform = getCurrentPlatform()

  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    import.meta.env.VITE_INVISIBLE_RECAPTCHA_KEY,
    {
      enterprise: true,
    }
  )

  const createAddress = useMutation({
    mutationFn: async (data: NewAddress) => {
      const country = data.location?.find(item =>
        item.types.includes(AddressTypeEnum.COUNTRY)
      )
      const state = data.location?.find(item =>
        item.types.includes(AddressTypeEnum.STATE)
      )

      const res = await api.payment.createAddress({
        data: {
          address: data.address ?? null,
          pincode: null,
          city: null,
          country: country
            ? { name: country.longText, isoCode: country.shortText }
            : null,
          state: state
            ? { name: state.longText, isoCode: state.shortText }
            : null,
        },
      })

      return res
    },
    onSuccess: res => {
      onAddressCreated(res)
    },
    onError: err => {
      mapErrorsV2(err, {
        setter: setError,
        fields: [
          ["address", "location"],
          ["pincode", "location"],
          ["city", "location"],
          ["country", "location"],
          ["state", "location"],
        ],
      })
    },
  })

  const signUpUser = async () => {
    const captcha = {
      value: await executeGoogleReCaptcha("auth"),
      type: CaptchaType.invisible,
    }

    const { firstName, lastName } = splitFullName(getValues("fullName") || "")

    const res = await api.auth.signUp({
      data: {
        captcha,
        source: "cpat",
        email: getValues("email").trim(),
        first_name: firstName,
        last_name: lastName,
        token_type: 1,
        platform: platform,
      },
    })

    setAuthInfo(res)

    trackingService.initUser(res)

    await createLead({
      email: res.user.email,
      fullName: getValues("fullName") || "",

      originDetails: {
        brand: "suraasa",
        product: "cpat",
        activityMode: "online",
        activityChannel: "website",
        activityType: "product_purchase",
        activityDetail: "direct_payment_method",
      },
      data: {
        uuid: res.user.uuid,
        source: trackingService.getSignupSource(),
      },
    })
  }

  const [emailAlreadyExists, setEmailAlreadyExist] = useState<boolean>(true)

  const sendOtp = async (uuid: string) => {
    const captcha = {
      value: await executeGoogleReCaptcha("auth"),
      type: CaptchaType.invisible,
    }

    const res = await api.settings.sendOTP({
      data: {
        medium: "email",
        purpose: "login",
        user: uuid,
        captcha,
      },
    })

    setOtpData(res)
    setOpenSignInEmailDialog(true)
  }

  const handleGetUserDetails = async (): Promise<FetchUserResponse | null> => {
    try {
      const res = await api.users.getUserDetails({
        data: {
          userId: getValues("email").trim(),
          product: 1,
        },
      })

      setEmailAlreadyExist(true)
      return res
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      setEmailAlreadyExist(false)
      return null
    }
  }

  const onFormSubmit = async () => {
    let captchaValue: string | null = null
    try {
      captchaValue = await executeGoogleReCaptcha("auth")
    } catch {
      toast.error("Please verify the captcha")
      return
    }

    if (!captchaValue) {
      toast.error("Please verify the captcha")
      return
    }

    try {
      let userDetailsLocalVariable: FetchUserResponse | null = null

      if (isEmail(getValues("email"))) {
        userDetailsLocalVariable = await handleGetUserDetails()
      }

      if (
        !userDetailsLocalVariable &&
        !isAuthenticated &&
        getValues("fullName")
      ) {
        await signUpUser()
        const addressData: NewAddress = {
          location: getValues("location"),
          address: null,
          country: null,
          state: null,
        }
        await createAddress.mutate(addressData)

        return
      } else {
        if (!userDetailsLocalVariable?.uuid) {
          throw new Error("User details not found")
        }

        await sendOtp(userDetailsLocalVariable?.uuid)
      }
    } catch (err) {
      if (err instanceof APIError) {
        if (err.errors.message) {
          toast.error(err.errors.message)
        }
        if (err.errors.fieldErrors?.email) {
          mapErrorsV2(err, {
            setter: setError,
            fields: [["email"], ["fullName"], ["location"]],
          })
        }
      }
    }
  }

  const locationValue = useWatch({
    control,
    name: "location",
  })

  useEffect(() => {
    const handleAddressCreation = async () => {
      if (isAuthenticated && locationValue && !createAddress.isPending) {
        const addressData: NewAddress = {
          location: locationValue,
          address: null,
          country: null,
          state: null,
        }
        await createAddress.mutate(addressData)
      }
    }

    handleAddressCreation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, locationValue])

  return (
    <div className="mx-auto mt-6 px-2 md:max-w-xl">
      {otpData && (
        <SignInEmailDialog
          otpData={otpData}
          close={() => {
            setOpenSignInEmailDialog(false)
          }}
          onVerify={async () => {
            const addressData: NewAddress = {
              location: getValues("location"),
              address: null,
              country: null,
              state: null,
            }

            await createAddress.mutate(addressData)

            setOpenSignInEmailDialog(false)
          }}
          onBack={() => {
            setOpenSignInEmailDialog(false)
          }}
          email={getValues("email")}
          open={openSignInEmailDialog}
        />
      )}
      <div className="mb-4 flex flex-row-reverse items-center gap-2 sm:flex-row">
        <img
          alt="cpat"
          onError={({ currentTarget }) => {
            currentTarget.onerror = null
            currentTarget.src = CourseFallback
          }}
          src={getProductImage({
            slug: CPAT_PRODUCT_SLUG,
            defaultImage: CourseFallback,
          })}
          className="size-[84px] shrink-0 overflow-hidden rounded-xl border border-surface-100 object-cover"
        />
        <Typography variant="title2">
          Comprehensive Pedagogy Assessment of Teachers
        </Typography>
      </div>
      <Divider weight="light" className="mb-4" />
      <Typography variant={"largeBody"} className="mb-3">
        Enter your Details
      </Typography>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          control={control}
          name="location"
          render={({ field: { onChange } }) => (
            <GoogleLocationSelect
              fullWidth
              label="City/State"
              required
              className="mb-2 w-full"
              placeHolder="Enter your location"
              errors={errors.location?.message}
              onChange={onChange}
            />
          )}
          rules={{
            required: { value: true, message: "Required" },
          }}
        />
        <TextField
          required
          label="Email"
          type="email"
          disabled={Boolean(isAuthenticated)}
          className="mb-2 !w-full"
          placeholder="E.g., name@example.com"
          helperText={
            "We'll use this email to give you access to the assessment."
          }
          errors={errors.email?.message}
          {...register("email", {
            required: { value: true, message: "Required" },
            validate: value => isEmail(value) || "Invalid email address",
          })}
        />
        {!isAuthenticated && !emailAlreadyExists && (
          <TextField
            required={!emailAlreadyExists}
            label="Full Name"
            autoFocus
            placeholder="E.g., John Doe"
            helperText={"This name will be used for creating your account"}
            errors={errors.fullName?.message}
            {...register("fullName", {
              required: !emailAlreadyExists ? "Required" : false,
            })}
          />
        )}

        {!isAuthenticated && (
          <Button
            color="black"
            type="submit"
            className="my-2 w-full"
            loading={createAddress.isPending || isSubmitting}
          >
            Submit Details
          </Button>
        )}
      </form>
    </div>
  )
}

export default BillingForm
